import {
    ChangeDetectorRef,
    Component, ElementRef, QueryList, ViewChild, ViewChildren, ViewEncapsulation,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
    ApolloService, Order_By,
} from 'src/gql-generated/generated';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { SentryService } from 'src/app/services/sentry.service';
import { UserService } from 'src/app/services/user.service';
import { OrganizationService } from 'src/app/services/organization.service';
import { ChatFeedService } from 'src/app/services/chat-feed.service';
import { DynamicDialogComponent } from '../shared/dynamic-dialog/dynamic-dialog.component';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from '@angular/cdk/scrolling';
import { NgIf, NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';

@Component({
    selector: 'app-chat-feed',
    templateUrl: './chat-feed.component.html',
    styleUrl: './chat-feed.component.scss',
    encapsulation: ViewEncapsulation.None,
    imports: [
        CdkVirtualScrollViewport,
        CdkFixedSizeVirtualScroll,
        NgIf,
        CdkVirtualForOf,
        NgClass,
        RouterLink,
        MatIcon,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        DynamicDialogComponent,
    ],
})
export class ChatFeedComponent {
    /**
     * Reference to the title update input fields
     */
    @ViewChildren('itemTitleInput') itemTitleInputs?: QueryList<ElementRef<HTMLInputElement>>;

    /**
     * Reference to the delete chat dialog
     * this reference is used to open the dialog and read the users actions
     */
    @ViewChild('deleteChatDialog') deleteChatDialog?: DynamicDialogComponent;

    /**
     * List of all chat feed items that the user has access to
     */
    chatFeed: ItemWithHovered[] = [];

    /**
     * ID of the item currently being edited (title update)
     */
    editingItemId?: string;

    /**
     * Total amount of items that could be retrieved by the user
     */
    totalItemCount?: number;

    /**
     * Offset for the next item retrieval
     * Also amount of items that have currently been retrieved
     */
    offset: number = 0;

    /**
     * True if items are currently being retrieved
     */
    isLoading: boolean = false;

    constructor(
        private apollo: ApolloService,
        private router: Router,
        public breakpoint: BreakpointService,
        private snackbar: SnackbarService,
        private sentry: SentryService,
        private user: UserService,
        public chatFeedService: ChatFeedService,
        public organization: OrganizationService,
        private cdr: ChangeDetectorRef,
    ) {
        // Reload chat feed when a new one has been created by the rag service
        this.chatFeedService.chatFeedChanged.subscribe(() => {
            this.refreshChatFeed();
        });
        // Reload chat feed when user switches organization
        this.organization.currentOrganization$.subscribe(() => {
            this.refreshChatFeed();
        });
        // Initial chat feed load
        this.loadChatFeed();
    }

    /**
     * Refresh the chat feed by resetting the list, offset and total item count
     * Afterwards calls for the first batch of chat feed to be retrieved
     */
    private refreshChatFeed(): void {
        // Reset state
        this.chatFeed = [];
        this.offset = 0;
        this.totalItemCount = undefined;

        // Load initial batch of chat items
        this.loadChatFeed();
    }

    /**
     * Check if new items should be retrieved based on the current index of the scroll
     * and the amount of items retrieved so far
     * Retrieves new chat items when user scrolls near the end of the current feed
     * @param index current index of the scroll
     */
    onScrolledIndexChange(index: number): void {
        if (index + 10 >= this.chatFeed.length) {
            this.loadChatFeed();
        }
    }

    /**
     * Loads chat feed items with limit on 50 based on offset
     * Also updates this.offset and this.totalItemCount
     */
    private async loadChatFeed(): Promise<void> {
        if (this.isLoading || (this.totalItemCount && (this.offset >= this.totalItemCount))) {
            return;
        }
        this.isLoading = true;
        try {
            const chatFeedResponse = await firstValueFrom(
                this.apollo.getChats({
                    where: {
                        title: { _is_null: false },
                        created_by_id: { _eq: this.user.profile.id },
                        Organization: {
                            id: { _eq: this.organization.currentOrganization$.value?.id },
                            Users: { id: { _eq: this.user.profile.id } },
                        },
                        deleted_at: { _is_null: true },
                    },
                    limit: 50,
                    offset: this.offset,
                    order_by: [{ last_modified_at: Order_By.Desc }],
                }),
            );

            this.chatFeed = this.chatFeed.concat(
                chatFeedResponse.data?.chats.map((item) => ({
                    id: item.id,
                    title: item.title,
                    hovered: false,
                })) || [],
            );
            this.offset += chatFeedResponse.data?.chats.length || 0;
            if (chatFeedResponse.data?.chats_aggregate.aggregate?.count) {
                this.totalItemCount = chatFeedResponse.data.chats_aggregate.aggregate.count;
            }
        } catch (error) {
            this.sentry.error(error);
            this.snackbar.error($localize`Kan chats niet laden`);
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Helper function that sets item.hovered = true
     * @param item item to set hovered for
     */
    onMouseEnter(item: ItemWithHovered): void {
        item.hovered = true;
    }

    /**
     * Helper function that sets item.hovered = false
     * @param item item to set hovered for
     */
    onMouseLeave(item: ItemWithHovered): void {
        item.hovered = false;
    }

    /**
     * Delete an item by setting the deleted_at field to the current timestamp
     * @param item item that needs to be deleted
     */
    async deleteItem(item: ItemWithHovered): Promise<void> {
        if (!this.deleteChatDialog) {
            return;
        }

        try {
            const accepted = await this.deleteChatDialog.open();
            if (accepted) {
                const updateChatResult = await firstValueFrom(this.apollo.updateChat({
                    id: item.id,
                    chatUpdates: {
                        deleted_at: 'now()',
                        deleted_by_id: this.user.profile.id,
                    },
                }));
                if (!updateChatResult.data?.update_chats_by_pk) {
                    throw new Error('Failed to delete chat');
                }

                this.refreshChatFeed();
                const successMessage = $localize`Chat succesvol verwijderd`;
                this.snackbar.success(successMessage);
                // Navigate to new chat page if the user is on the deleted chat page
                if (this.router.url.includes(item.id)) {
                    this.router.navigate(['chat', 'new']);
                }
            }
        } catch (error) {
            this.sentry.error(error);
            const errorMessage = $localize`Chat verwijderen mislukt`;
            this.snackbar.error(errorMessage);
        }
    }

    /**
     * Helper function for entering a title edit input field to focus and select the input field
     * @param item item to edit
     */
    enterTitleEdit(item: ItemWithHovered): void {
        this.editingItemId = item.id;
        // Manually flush the DOM changes so that the item we're editing can render its <input> tag
        // Needed because Firefox is slow with updating DOM
        this.cdr.detectChanges();

        setTimeout(() => {
            if (!this.itemTitleInputs) {
                return;
            }

            // Find the correct <input> field using the item id
            // this item id was added as extra attribute in the html
            const inputEl = this.itemTitleInputs.find(
                (el) => el.nativeElement.getAttribute('item-id') === item.id,
            )?.nativeElement;

            if (inputEl) {
                inputEl.focus();
                inputEl.select();
            }
        });
    }

    /**
     * Exit a title edit field which updates the title of the item in the backend if it has changed
     * @param event produced by pressing enter or unfocussing the input field
     * @param item item to edit
     */
    async exitTitleEdit(event: Event, item: ItemWithHovered): Promise<void> {
        const input = event.target as HTMLInputElement;
        const newTitle = input.value.trim();

        // Only update if title has changed
        if (newTitle === item.title) {
            this.editingItemId = undefined;
            return;
        }

        try {
            const updateChatResult = await firstValueFrom(this.apollo.updateChat({
                id: item.id,
                chatUpdates: {
                    title: newTitle,
                    updated_at: 'now()',
                    updated_by_id: this.user.profile.id,
                },
            }));

            if (!updateChatResult.data?.update_chats_by_pk) {
                throw new Error('Failed to update chat title');
            }


            this.refreshChatFeed();
            const successMessage = $localize`Chat succesvol aangepast`;
            this.snackbar.success(successMessage);
        } catch (error) {
            this.sentry.error(error);
            const errorMessage = $localize`Chat aanpassen mislukt`;
            this.snackbar.error(errorMessage);
        }
        this.editingItemId = undefined;
    }
}

interface ItemWithHovered {
    id: string;
    title: string;
    hovered: boolean;
}
